import {useRef, type MouseEvent} from 'react';
import {User} from 'lucide-react';
import {useIntl} from 'react-intl';
import {initialsOf} from 'ch3-ui-lib';
import {insightsMessages as m} from '../insightsMessages';
import {logPlaybackSeek} from '../insightsLog';
import {lucide} from '../insightsUi';
import {usePlayback} from '../player/PlaybackContext';
import {laneClass, laneTextClass, type ResolvedSegment, type Speaker, type SpeakerCast, type SpeakerSide} from '../transcript/speakers';

interface SpeakerTimelineProps {
    segments: readonly ResolvedSegment[];
    cast: SpeakerCast;
    duration: number;
}

const LANES: SpeakerSide[] = ['client', 'agent'];

const LaneLabel = ({speaker}: {speaker: Speaker}) => {
    const initials = speaker.known ? initialsOf(speaker.name) : '';
    return (
        <span
            data-testid={`insights-lane-label-${speaker.side}`}
            className={`flex h-3 items-center justify-center text-micro ${laneTextClass[speaker.palette]}`}
        >
            {initials || <User size={12} className={lucide}/>}
        </span>
    );
};

export const SpeakerTimeline = ({segments, cast, duration}: SpeakerTimelineProps) => {
    const intl = useIntl();
    const playback = usePlayback();
    const ref = useRef<HTMLDivElement>(null);
    if (duration <= 0 || segments.length === 0) return <span aria-hidden="true"/>;

    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!playback.canSeek || !rect || rect.width === 0) return;
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        playback.seek(ratio * duration);
        logPlaybackSeek('timeline');
    };

    const progress = Math.min(100, (playback.currentTime / duration) * 100);

    return (
        <div
            role="img"
            aria-label={intl.formatMessage(m.timelineLabel)}
            data-testid="insights-speaker-timeline"
            className="col-span-2 grid grid-cols-[32px_minmax(0,1fr)] items-center gap-x-3"
        >
            <div aria-hidden="true" className="flex flex-col gap-0.5">
                {LANES.map((side) => <LaneLabel key={side} speaker={cast[side]}/>)}
            </div>
            <div
                ref={ref}
                onClick={onClick}
                className={`relative flex flex-col gap-0.5 ${playback.canSeek ? 'cursor-pointer' : ''}`}
            >
                {LANES.map((side) => (
                    <div key={side} className="flex h-3 items-center">
                        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
                            {segments.filter((segment) => segment.speaker.side === side).map((segment) => (
                                <span
                                    key={segment.segmentId}
                                    className={`absolute inset-y-0 rounded-full ${laneClass[cast[side].palette]}`}
                                    style={{
                                        left: `${(segment.startTime / duration) * 100}%`,
                                        width: `${Math.max(0.4, ((segment.endTime - segment.startTime) / duration) * 100)}%`,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                ))}
                {playback.canSeek && (
                    <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 rounded-full bg-ink" style={{left: `${progress}%`}}/>
                )}
            </div>
        </div>
    );
};
